import { Link } from "react-router-dom";
import { Mail, ArrowUpRight, ArrowLeft } from "lucide-react";
import FoxMotif from "@/components/site/FoxMotif";
import { useContent } from "@/contexts/ContentContext";

export default function PrivacyPage() {
  const { content } = useContent();
  const c = content.footer || {};
  const paragraphs = (c.privacy_policy || "").split(/\n\n+/).filter(Boolean);
  const email = c.email || "hello@playfoxstudio.com";
  const lastUpdated = (c.privacy_last_updated || "").trim();

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
      <header className="border-b border-[hsl(var(--border))] bg-white">
        <div className="pf-container h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 rounded-md"
            aria-label="Play Fox Studio home"
          >
            <FoxMotif size={32} testId="privacy-fox" />
            <span className="pf-display text-[17px] tracking-tight">
              Play Fox <span className="text-[#F35A1F]">Studio</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="pf-container py-12 sm:py-16">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2">
            <span className="pf-mono text-[11px] uppercase tracking-wider text-[#D94A14]">Legal</span>
            <span className="h-px w-10 bg-[hsl(var(--border))]" />
          </div>
          <h1
            data-testid="privacy-title"
            className="mt-3 pf-display text-[40px] sm:text-[56px] leading-[1.05] tracking-tight"
          >
            Privacy <span className="text-[#F35A1F]">Policy.</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
            A short, plain-English overview of how Play Fox Studio handles your data.
          </p>

          <div className="mt-6 inline-flex flex-wrap items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-white px-4 py-3 pf-shadow-sm">
            {lastUpdated && (
              <span
                data-testid="privacy-last-updated"
                className="pf-mono text-[11px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]"
              >
                Last updated: {lastUpdated}
              </span>
            )}
            {lastUpdated && <span className="h-4 w-px bg-[hsl(var(--border))]" />}
            <a
              href={`mailto:${email}`}
              data-testid="privacy-contact-email"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D94A14] hover:text-[#F35A1F]"
            >
              <Mail className="h-4 w-4" />
              {email}
            </a>
          </div>

          <article
            data-testid="privacy-body"
            className="mt-10 space-y-5 text-base sm:text-[17px] leading-relaxed text-[hsl(var(--foreground))]"
          >
            {paragraphs.length === 0 ? (
              <p className="text-[hsl(var(--muted-foreground))]">
                No privacy policy has been published yet.
              </p>
            ) : (
              paragraphs.map((p, i) => (
                <p key={i} className="whitespace-pre-line">
                  {p}
                </p>
              ))
            )}
          </article>

          <div className="mt-12 flex items-center justify-between border-t border-[hsl(var(--border))] pt-6">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Questions or feedback? Email <a className="text-[#D94A14] underline" href={`mailto:${email}`}>{email}</a>.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-xl border border-[hsl(var(--border))] bg-white px-4 py-2 text-sm hover:bg-[hsl(var(--accent))]"
            >
              Back to home
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
